import { useState } from "react";
import { ImageIcon } from "lucide-react";
import { useApi } from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { PosterPreview } from "@/components/site/EventPoster";
import { drawWeekPoster } from "@/lib/poster";
import { EVENT_TYPES, EVENT_TYPE_PLURAL } from "@/lib/eventTypes";

// Sunday 23:59:59 of the current week (Monday → Sunday)
export const endOfWeek = () => {
  const d = new Date();
  d.setDate(d.getDate() + ((7 - d.getDay()) % 7));
  d.setHours(23, 59, 59, 999);
  return d;
};

const PERIODS = {
  week: { label: "Cette semaine", line1: "CETTE", line2: "SEMAINE", days: 6 },
  weekend: { label: "Ce week-end", line1: "CE", line2: "WEEK-END", days: 2 },
};

const periodRange = (period) => {
  const end = endOfWeek();
  const start = new Date(end);
  start.setDate(end.getDate() - PERIODS[period].days);
  start.setHours(0, 0, 0, 0);
  return { start, end };
};

const rangeText = ({ start, end }) => {
  const fmt = (d, o) => d.toLocaleDateString("fr-FR", o).replace(".", "");
  return `du ${fmt(start, { weekday: "short", day: "numeric" })} au ${fmt(end, { weekday: "short", day: "numeric", month: "long" })}`;
};

const chip = (on) => `h-9 px-3 border text-[11px] uppercase tracking-[0.15em] font-bold transition-colors duration-200 ${on ? "bg-white text-black border-white" : "border-white/30 text-zinc-200 hover:border-white"}`;

const Choices = ({ items, value, onChange, testId }) => (
  <div className="flex flex-wrap gap-2" data-testid={testId}>
    {items.map((i) => (
      <button key={i.key} type="button" onClick={() => onChange(i.key)} aria-pressed={value === i.key} data-testid={`${testId}-${i.key}`} className={chip(value === i.key)}>{i.label}</button>
    ))}
  </div>
);

const PosterBody = ({ initialType }) => {
  const [period, setPeriod] = useState("week");
  const [type, setType] = useState(initialType || "all");
  const { data, isLoading } = useApi("/events", { when: "upcoming", limit: 60, type: type === "all" ? undefined : type });
  const range = periodRange(period);
  const events = (Array.isArray(data) ? data : []).filter((e) => { const d = new Date(e.date); return d >= range.start && d <= range.end; });
  const p = PERIODS[period];
  const sub = [type === "all" ? null : EVENT_TYPE_PLURAL[type], "sur LGTDZ", rangeText(range)].filter(Boolean).join(" · ");
  return (
    <>
      <Choices items={Object.entries(PERIODS).map(([key, v]) => ({ key, label: v.label }))} value={period} onChange={setPeriod} testId="week-poster-period" />
      <Choices items={[{ key: "all", label: "Tout" }, ...EVENT_TYPES.map((t) => ({ key: t.key, label: t.plural }))]} value={type} onChange={setType} testId="week-poster-type" />
      {isLoading ? null : events.length ? (
        <PosterPreview format="story" testId="week-poster" title={`${p.label} sur LGTDZ`}
          name={`lgtdz-${period}-${type}-${events.map((e) => e.id).join("-")}`.slice(0, 90)}
          draw={(canvas) => drawWeekPoster(canvas, events, { line1: p.line1, line2: p.line2, sub })} />
      ) : (
        <p className="text-zinc-300 text-sm py-6" data-testid="week-poster-empty">Aucun événement {type === "all" ? "" : `« ${EVENT_TYPE_PLURAL[type].toLowerCase()} » `}{period === "week" ? "cette semaine" : "ce week-end"}.</p>
      )}
    </>
  );
};

// « Affiche de la semaine / du week-end », optionally for one category (soirées club, festivals…)
export const WeekPosterDialog = ({ className, label = "Affiche de la semaine", initialType, testId = "week-poster-btn" }) => (
  <Dialog>
    <DialogTrigger data-testid={testId} className={className}>
      <ImageIcon className="w-3.5 h-3.5" /> {label}
    </DialogTrigger>
    <DialogContent className="rounded-none bg-black border-white/10 max-w-md max-h-[92vh] overflow-y-auto" data-testid="week-poster-dialog">
      <DialogTitle className="font-display uppercase text-2xl text-white">Affiche à partager</DialogTitle>
      <DialogDescription className="text-zinc-300 text-sm -mt-2">Les dates de la semaine ou du week-end en une story, pour tout l'agenda ou une seule catégorie.</DialogDescription>
      <PosterBody initialType={initialType} />
    </DialogContent>
  </Dialog>
);
